import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DonorsTable from "../../components/donors/DonorsTable";
import AddDonorModal from "../../components/donors/AddDonorModal";
import EditDonorModal from "../../components/donors/EditDonorModal";
import ViewDonorModal from "../../components/donors/ViewDonorModal";
import AddDonationModal from "../../components/donors/AddDonationModal";
import SidebarNav from "../../components/MasterSideBar"; // Updated reference
import TopBar from "../../components/TopbarNav";
import { PlusCircle } from "lucide-react";
import useDebounce from "../../hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import { FiUsers, FiCreditCard, FiDollarSign } from "react-icons/fi";

export default function DonorsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [donors, setDonors] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAddDonationModalOpen, setIsAddDonationModalOpen] = useState(false);
  const [selectedDonorId, setSelectedDonorId] = useState(null);

  const [dashboardStats, setDashboardStats] = useState({
    totalActiveDonors: 0,
    totalDonationsThisYear: 0,
    averageDonation: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const API_URL = "https://ims.holdthechild.net";
    try {
      if (!token) return;
      const summaryRes = await axios.get(`${API_URL}/api/donors/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardStats(summaryRes.data.data);
    } catch (e) {
      console.error("Failed to fetch dashboard data:", e);
    }
  }, []);

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const API_URL = "https://ims.holdthechild.net";
    try {
      if (!token) throw new Error("Authentication token not found.");
      const params = {
        page: currentPage,
        limit: 10,
        sortBy: "createdAt",
        sortOrder: "desc",
        status: "active",
      };
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;

      const response = await axios.get(`${API_URL}/api/donors`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data, pagination } = response.data;
      setDonors(data);
      setPaginationInfo(pagination);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to fetch donors.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm]);

  useEffect(() => {
    fetchDonors();
    fetchDashboardData();
  }, [fetchDonors, fetchDashboardData]);

  const handleOpenAddModal = () => setIsAddModalOpen(true);
  const handleOpenViewModal = (donorId) => {
    setSelectedDonorId(donorId);
    setIsViewModalOpen(true);
  };
  const handleOpenEditModal = (donorId) => {
    setSelectedDonorId(donorId);
    setIsEditModalOpen(true);
  };
  const handleOpenAddDonationModal = (donorId) => {
    setSelectedDonorId(donorId);
    setIsAddDonationModalOpen(true);
  };

  const handleRefresh = (message) => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    setIsAddDonationModalOpen(false);
    setSelectedDonorId(null);
    setSuccessMsg(message || "Operation successful!");
    fetchDonors();
    fetchDashboardData();
  };

  const StatCard = ({ title, value, icon, currency = false }) => {
    const formattedValue = currency
      ? `USD ${new Intl.NumberFormat().format(value)}`
      : new Intl.NumberFormat().format(value);
    return (
      <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm flex flex-col justify-between items-start transition-all hover:shadow-md">
        <div className="flex items-center gap-2 mb-2 text-sm font-bold text-gray-400 uppercase tracking-tight">
          <span className="p-2 bg-orange-50 rounded-lg text-orange-600">{icon}</span>
          <span>{title}</span>
        </div>
        <p className="text-3xl font-black text-gray-900 tracking-tighter">{formattedValue}</p>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* 1. Master Sidebar */}
      <SidebarNav visible={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* 2. Content Wrapper */}
      <div className="flex-1 flex flex-col md:ml-64 transition-all duration-300">
        
        {/* 3. TopBar */}
        <TopBar 
          onSearchChange={setSearchTerm} 
          searchTerm={searchTerm} 
          onSidebarToggle={() => setSidebarOpen(true)} 
        />

        <main className="p-4 md:p-8 mt-16 lg:mt-20">
          {/* Notifications */}
          {successMsg && (
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] bg-green-600 text-white px-6 py-3 rounded-full shadow-2xl font-bold animate-bounce">
              ✅ {successMsg}
            </div>
          )}

          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">
                Donor Management
              </h1>
              <p className="text-gray-500 text-sm">Track active partners and organizational contributions.</p>
            </div>

            <button
              onClick={handleOpenAddModal}
              className="flex items-center justify-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-700 shadow-lg shadow-orange-200 transition-all active:scale-95"
            >
              <PlusCircle size={20} />
              Add New Donor
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <StatCard
              title="Active Donors"
              value={dashboardStats.totalActiveDonors}
              icon={<FiUsers size={20} />}
            />
            <StatCard
              title="Donations (YTD)"
              value={dashboardStats.totalDonationsThisYear}
              icon={<FiDollarSign size={20} />}
              currency
            />
            <StatCard
              title="Average Value"
              value={dashboardStats.averageDonation}
              icon={<FiCreditCard size={20} />}
              currency
            />
          </div>

          {/* Table Container */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="py-20 text-center">
                 <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto mb-4"></div>
                 <p className="text-gray-400 font-medium">Loading donor database...</p>
              </div>
            ) : (
              <DonorsTable
                donors={donors}
                paginationInfo={paginationInfo}
                onPageChange={setCurrentPage}
                onView={handleOpenViewModal}
                onEdit={handleOpenEditModal}
                onAddDonation={handleOpenAddDonationModal}
                currentSearchTerm={debouncedSearchTerm}
              />
            )}
          </div>

          {/* Modals */}
          {isAddModalOpen && (
            <AddDonorModal
              closeForm={() => setIsAddModalOpen(false)}
              onDonorAdded={() => handleRefresh("Donor created successfully!")}
            />
          )}
          {isEditModalOpen && selectedDonorId && (
            <EditDonorModal
              donorId={selectedDonorId}
              closeModal={() => setIsEditModalOpen(false)}
              onUpdateSuccess={() => handleRefresh("Donor updated successfully!")}
            />
          )}
          {isViewModalOpen && selectedDonorId && (
            <ViewDonorModal donorId={selectedDonorId} closeModal={() => setIsViewModalOpen(false)} />
          )}
          {isAddDonationModalOpen && selectedDonorId && (
            <AddDonationModal
              donorId={selectedDonorId}
              closeModal={() => setIsAddDonationModalOpen(false)}
              onDonationAdded={() => handleRefresh("Donation added successfully!")}
            />
          )}
        </main>
      </div>
    </div>
  );
}